import React, { useState } from "react";
import styles from "./PersonalPage.module.css";
import { useHistory } from "react-router-dom";
import { useAccount } from "../utils/AccountInfoContext";
import { usePersonal } from "./usePersonal";
import Header from "../components/Header";
import Button from "../components/Button";
import { PersonalNameItem } from "../components/PersonalNameItem/PersonalNameItem";
import { PersonalItem } from "../components/PersonalItem/PersonalItem";
import { AddCoIboModal } from "../components/AddCoIboModal/AddCoIboModal";
import { PersonalPasswordItem } from "../components/PersonalPasswordItem/PersonalPasswordItem";
import AccountHeader from "components/AccountHeader";
import SwitchUserDropdown from "components/SwitchUserDropdown";
import { formattedPhone } from "components/shared/formattedPhone";
import Modal from "components/Modal";
import Cookies, { set } from "js-cookie";
import mobile from "mobileBridge";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

import parsePhoneNumber from "libphonenumber-js";
import environment from "../environment";

/**
 * Personal Page
 */

export function PersonalPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    accountData,
    usersData,
    activeUser,
    setActiveUserInfo,
    setStoredUserId,
    refetchUsersData,
  } = useAccount();
  const queryClient = useQueryClient();

  const {
    updateName,
    updateEmail,
    updatePhrase,
    updatePhone,
    updatePassword,
    createSecondaryContact,
  } = usePersonal();
  const [currentlyEditing, setCurrentlyEditing] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    email: "",
    phrase: "",
    phone: "",
    password: "",
    iboEmail: "",
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [maskedPassword, setMaskedPassword] = useState("*******"); // State for masked password

  const openDropdown = () => {
    if (mobile.isApp()) {
      const users = usersData.map((user) => {
        const contact = accountData.contacts.find(
          (c) => c.contactId === user.contactId
        );

        return {
          userId: user.userId,
          contactId: user.contactId,
          name: `${contact?.firstName || user.strFirst} ${
            contact?.lastName || user.strLast
          }`,
        };
      });

      mobile
        .postMessage("change-user-native", users, true)
        .then((selectedUser) => {
          const userData = users.filter((o) => o.userId === selectedUser);

          const newUser = userData.map((user) => {
            const [strFirst, strLast] = user.name.split(" ");

            const contact = accountData.contacts.find(
              (c) => c.contactId === user.contactId
            );
            return {
              ...user,
              ...contact,
              firstName: contact?.firstName || strFirst,
              lastName: contact?.lastName || strLast,
              iboNum: accountData.account.ibo,
            };
          });
          setStoredUserId(null);
          setActiveUserInfo(newUser[0]);
        });
    } else {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleSaveUser = (user) => {
    setActiveUserInfo(user);
    Cookies.set("community-user-id", user.userId, {
      path: "/",
      domain: ".wwdb.com",
    });

    closeDropdown();
  };

  // Add Co-IBO
  const handleAddCoIbo = async (info) => {
    const validationErrors = validateInputs({
      iboEmail: info.email,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (info.email === activeUser.email) {
      setErrors({
        email: t("The provided email matches"),
      });
      setShowErrorModal(true);
      return;
    }

    const secondaryContact = {
      firstName: info.firstName,
      middleName: info.middleName,
      lastName: info.lastName,
      email: info.email,
      phone: info.phone.replace(/\D/g, ""),
    };

    try {
      await createSecondaryContact.mutateAsync(secondaryContact);

      // If success reset errors anc close modal
      setErrors({});
      setIsAddModalOpen(false);
      refetchUsersData();
      queryClient.invalidateQueries("account");
      queryClient.invalidateQueries("usersInfo");
    } catch (error) {
      console.error(t("Failed to add secondary contact"));
      setShowErrorModal(true);
    }
  };

  const hidePassword = (input) => "*".repeat(input?.length);

  const toggleEdit = (item) => {
    if (currentlyEditing === item) {
      setCurrentlyEditing(null);
      setErrors({
        firstName: "",
        email: "",
        phrase: "",
        phone: "",
        password: "",
      });
    } else {
      setCurrentlyEditing(item);
      setErrors({
        firstName: "",
        email: "",
        phrase: "",
        phone: "",
        password: "",
      });
    }
  };

  // Extract date from ISO
  const extractDate = (dateString) => {
    const date = new Date(dateString);
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
    return utcDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const validateInputs = (inputs, existingEmails = []) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (inputs.firstName !== undefined) {
      if (inputs.firstName === "") {
        errors.firstName = t("Please enter a first name");
      }
    }

    if (inputs.email !== undefined) {
      if (inputs.email && !emailRegex.test(inputs.email)) {
        errors.email = t("Please enter a valid email address");
      }
      if (inputs.email === "") {
        errors.email = t("Please enter an email address");
      }
      if (existingEmails.includes(inputs.email)) {
        errors.email = t("Please enter a new email");
        setShowErrorModal(true);
      }
    }

    if (inputs.phrase !== undefined) {
      if (!inputs.phrase) {
        errors.phrase = t("Please enter a phrase");
      }
    }

    if (inputs.phone !== undefined) {
      if (!inputs.phone) {
        errors.phone = t("Please enter a phone number");
      } else {
        const cleanedPhone = inputs.phone.replace(/\D/g, "");
        if (cleanedPhone.length !== 10) {
          errors.phone = t("Please enter a valid phone number");
        }
      }
    }

    return errors;
  };

  // Update name
  const handleUpdateName = async (newName) => {
    const validationErrors = validateInputs({ firstName: newName.firstName });

    if (Object.keys(validationErrors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        firstName: validationErrors.firstName || "",
      }));
      return;
    }

    try {
      await updateName.mutateAsync({
        contactId: activeUser.contactId,
        nameData: {
          firstName: newName.firstName,
          middleName: newName.middleName,
        },
      });
      setErrors((prevErrors) => ({
        ...prevErrors,
        firstName: "",
      }));
      setCurrentlyEditing(null);
    } catch (error) {
      console.error(t("Failed to update name"), error);
    }
  };

  const handleUpdateEmail = async (newEmail) => {
    const existingEmails = accountData.contacts.map((contact) => contact.email);
    const validationErrors = validateInputs(
      { email: newEmail },
      existingEmails
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: validationErrors.email || "",
      }));
      setShowErrorModal(true);
      return;
    }

    try {
      const response = await updateEmail.mutateAsync({
        contactId: activeUser.contactId,
        email: newEmail,
      });

      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "",
      }));
      setCurrentlyEditing(null);
    } catch (error) {
      console.error(t("Failed to update email"), error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: t("Failed to update email, try again"),
      }));
      setShowErrorModal(true);
    }
  };

  // Update Phone
  const handleUpdatePhone = async (phone) => {
    let parse = parsePhoneNumber(phone, environment.WEB_APP_COUNTRY);
    let isPossible = parse ? parse.isPossible(phone) : false;

    if (!isPossible) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "Please enter a valid phone number",
      }));
      return;
    }

    let _phone = parse.nationalNumber;

    try {
      await updatePhone.mutateAsync({
        contactId: activeUser.contactId,
        phoneData: { phone: _phone.replace(/\D/g, "") },
      });
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "",
      }));
      setCurrentlyEditing(null);
    } catch (error) {
      console.error(t("Failed to update phone"), error);
    }
  };

  // Update password
  const handleUpdatePassword = async (newPassword) => {
    try {
      await updatePassword.mutateAsync({
        passwordData: { password: newPassword },
      });
      setMaskedPassword(hidePassword(newPassword)); // Update the masked password state
      setCurrentlyEditing(null);
    } catch (error) {
      console.error(t("Failed to update password"), error);
    }
  };

  // Update Phrase
  const handleUpdatePhrase = async (newPhrase) => {
    const validationErrors = validateInputs({ phrase: newPhrase });

    if (Object.keys(validationErrors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phrase: validationErrors.phrase || "",
      }));
      return;
    }

    try {
      await updatePhrase.mutateAsync({
        phraseData: { phrase: newPhrase },
      });
      setErrors((prevErrors) => ({
        ...prevErrors,
        phrase: "",
      }));
      setCurrentlyEditing(null);
    } catch (error) {
      console.error(t("Failed to update phrase"), error);
    }
  };

  const contactCount = accountData?.contacts?.length;

  const handleCloseAddIBO = () => {
    setErrors({});
    setIsAddModalOpen(false);
  };

  const handleHeaderBackClick = () => {
    history.push("/");
  };

  return (
    <div className={styles.container}>
      <Header header={t("Settings")} onClick={handleHeaderBackClick} />
      <div className={styles.shrinkHeader}>
        <AccountHeader
          onClick={openDropdown}
          activeUser={activeUser?.name || ""}
          iboNum={activeUser?.iboNum}
          currentPage="personalpage"
          contactCount={contactCount}
          onAddIBOClick={() => setIsAddModalOpen(true)}
        />
        {isDropdownOpen && (
          <SwitchUserDropdown
            users={usersData.map((user) => {
              const contact = accountData.contacts.find(
                (c) => c.contactId === user.contactId
              );
              return {
                ...user,
                ...contact,
                firstName: contact?.firstName || user.strFirst,
                lastName: contact?.lastName || user.strLast,
                // iboNum: accountData.account.ibo,
              };
            })}
            onSave={handleSaveUser}
            activeUser={activeUser}
            isPersonalPage={true}
          />
        )}
        <div className={styles.spacer} />
        <AddCoIboModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddIBO}
          onSave={(info) => handleAddCoIbo(info)}
          activeUser={activeUser}
          showModal={() => setShowErrorModal(true)}
        />
      </div>

      <div className={styles.item}>
        <PersonalNameItem
          label={t("Name")}
          name={{
            firstName: activeUser.firstName,
            middleName: activeUser.middleName,
            lastName: activeUser.lastName,
          }}
          onSave={handleUpdateName}
          isEditing={currentlyEditing === "Name"}
          onToggleEdit={() => toggleEdit("Name")}
          error={errors.firstName}
        />
      </div>
      <div className={styles.item}>
        <PersonalItem
          editable={true}
          onVerify={true}
          verified={activeUser.isPhoneVerified}
          info={formattedPhone(activeUser.phone)}
          onSave={handleUpdatePhone}
          label={t("Phone")}
          isEditing={currentlyEditing === "Phone"}
          onToggleEdit={() => toggleEdit("Phone")}
          error={errors.phone}
          isPhoneNumber={true}
        />
      </div>
      <div className={styles.item}>
        <PersonalItem
          editable={true}
          onVerify={true}
          verified={activeUser.isEmailVerified}
          info={activeUser.email}
          onSave={handleUpdateEmail}
          label={t("Email")}
          isEditing={currentlyEditing === "Email"}
          onToggleEdit={() => toggleEdit("Email")}
          error={errors.email || errors.server}
          isPhoneNumber={false}
        />
      </div>
      <div className={styles.loginHeader}>{t("Login")}</div>
      <div className={styles.item}>
        <PersonalItem
          editable={false}
          onVerify={false}
          info={extractDate(accountData?.account?.nextBilledOn)}
          onSave={() => alert("Saved")}
          label={t("Renewal Date")}
          error={""}
          isPhoneNumber={false}
        />
      </div>

      <div className={styles.item}>
        <PersonalItem
          editable={false}
          onVerify={false}
          info={accountData?.account?.username}
          onSave={() => alert("Saved")}
          label={t("Username")}
          error={""}
          isPhoneNumber={false}
        />
      </div>

      <div className={styles.item}>
        <PersonalPasswordItem
          editable={true}
          password={maskedPassword} // Use the masked password state
          onSave={handleUpdatePassword}
          label={t("Password")}
          isEditing={currentlyEditing === "Password"}
          onToggleEdit={() => toggleEdit("Password")}
        />
      </div>

      <div className={styles.item}>
        <PersonalItem
          editable={true}
          // onVerify={false}
          info={accountData?.account?.rememberPhrase || ""}
          onSave={handleUpdatePhrase}
          label={t("Remember Phrase")}
          isEditing={currentlyEditing === "Remember Phrase"}
          onToggleEdit={() => toggleEdit("Remember Phrase")}
          error={errors.phrase}
          isPhoneNumber={false}
        />
      </div>

      {showErrorModal && (
        <Modal isOpen={showErrorModal}>
          <div className={styles.errorModal}>
            <p className={styles.text}>{t("Email already exists")}</p>
          </div>
          <div className={styles.button}>
            <Button
              buttonText={t("Okay")}
              onClick={() => {
                setShowErrorModal(false);
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

export default PersonalPage;
